import { useEffect, useMemo, useState } from "react";
import { Bluetooth, Loader2, RefreshCw, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useBleScan, rssiToBars, ScannedDevice } from "@/hooks/use-ble-scan";

interface BTScanDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (device: ScannedDevice) => void;
  /** Service UUIDs that mark a device as a "printer". Used to highlight + filter. */
  printerServices: string[];
  /** "receipt" | "label" — used for header copy. */
  printerType: "receipt" | "label";
}

export function BTScanDialog({
  open,
  onClose,
  onSelect,
  printerServices,
  printerType,
}: BTScanDialogProps) {
  const { devices, scanning, error, rescan, stop } = useBleScan();
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Auto-start scan when dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setShowAll(false);
    rescan();
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const printerSet = useMemo(
    () => new Set(printerServices.map((s) => s.toLowerCase())),
    [printerServices]
  );

  const isPrinter = (d: ScannedDevice) =>
    d.serviceUuids.some((u) => printerSet.has(u)) ||
    /printer|pos|thermal|tspl|zpl|escpos/i.test(d.name);

  const visible = useMemo(() => {
    return showAll ? devices : devices.filter(isPrinter);
  }, [devices, showAll, printerSet]);

  const printerCount = useMemo(() => devices.filter(isPrinter).length, [devices, printerSet]);

  const handleSelect = (d: ScannedDevice) => {
    setSelectedId(d.deviceId);
    stop();
    onSelect(d);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          stop();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md p-0 rounded-3xl overflow-hidden bg-white dark:bg-[#1A1614] border border-slate-200 dark:border-[#454545]">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 dark:border-[#2B2726]">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-black uppercase tracking-tighter text-[#0F172A] dark:text-[#EEEAE3]">
              {printerType === "receipt" ? "Receipt" : "Label"}{" "}
              <span className="text-[#8B1A1A]">Printer</span>
            </DialogTitle>
            {/* Rescan only — close X comes from <DialogContent> primitive. */}
            <button
              onClick={() => rescan()}
              disabled={scanning}
              className="rounded-full p-2 mr-8 hover:bg-slate-100 dark:hover:bg-[#1F1B19] disabled:opacity-50"
              aria-label="Rescan"
            >
              <RefreshCw className={`h-4 w-4 text-slate-600 dark:text-[#EEEAE3] ${scanning ? "animate-spin" : ""}`} />
            </button>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-[#8A8A92] mt-1">
            {scanning ? (
              <>
                <Loader2 className="inline h-3 w-3 mr-1 animate-spin" />
                Scanning for nearby devices…
              </>
            ) : (
              <>Scan complete · {visible.length} device{visible.length === 1 ? "" : "s"} shown</>
            )}
          </p>
        </div>

        {/* Filter toggle */}
        <div className="px-5 pt-3 pb-1 flex items-center gap-2">
          <button
            onClick={() => setShowAll(false)}
            className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
              !showAll
                ? "bg-[#8B1A1A] text-white"
                : "bg-slate-100 dark:bg-[#1F1B19] text-slate-600 dark:text-[#8A8A92]"
            }`}
          >
            <Printer className="inline h-3.5 w-3.5 mr-1" />
            Printers ({printerCount})
          </button>
          <button
            onClick={() => setShowAll(true)}
            className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
              showAll
                ? "bg-[#8B1A1A] text-white"
                : "bg-slate-100 dark:bg-[#1F1B19] text-slate-600 dark:text-[#8A8A92]"
            }`}
          >
            <Bluetooth className="inline h-3.5 w-3.5 mr-1" />
            All ({devices.length})
          </button>
        </div>

        {/* Device list */}
        <div className="px-2 pt-2 pb-4 max-h-[55vh] overflow-y-auto">
          {error && (
            <div className="mx-3 my-3 p-3 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {visible.length === 0 && !error ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <Bluetooth className="h-10 w-10 text-slate-300 dark:text-[#454545] mb-3" />
              <p className="text-sm font-semibold text-[#0F172A] dark:text-[#EEEAE3]">
                {scanning ? "Searching…" : "No devices found"}
              </p>
              <p className="text-xs text-slate-500 dark:text-[#8A8A92] mt-1 max-w-xs">
                {scanning
                  ? "Make sure your printer is powered on and in pairing mode."
                  : showAll
                  ? "No Bluetooth devices found nearby. Tap Rescan to try again."
                  : "Couldn't find a printer. Tap All to show every Bluetooth device, or power on your printer and tap Rescan."}
              </p>
            </div>
          ) : (
            visible.map((d) => {
              const bars = rssiToBars(d.rssi);
              const printerLike = isPrinter(d);
              const isSelected = selectedId === d.deviceId;
              return (
                <button
                  key={d.deviceId}
                  onClick={() => handleSelect(d)}
                  disabled={isSelected}
                  className={`w-full text-left flex items-center gap-3 p-3 mb-1 rounded-xl border transition-colors ${
                    isSelected
                      ? "bg-[#8B1A1A]/10 border-[#8B1A1A]/40"
                      : "border-transparent hover:bg-slate-50 dark:hover:bg-[#1F1B19] active:scale-[0.99]"
                  }`}
                >
                  <div
                    className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                      printerLike
                        ? "bg-[#8B1A1A]/15 text-[#8B1A1A]"
                        : "bg-slate-100 dark:bg-[#1F1B19] text-slate-500 dark:text-[#8A8A92]"
                    }`}
                  >
                    {printerLike ? <Printer className="h-4 w-4" /> : <Bluetooth className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-[#0F172A] dark:text-[#EEEAE3] truncate">
                        {d.name}
                      </p>
                      {printerLike && (
                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#A8851F] dark:text-[#D4AF37] border border-[#D4AF37]/30">
                          Printer
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 dark:text-[#6B6B72] font-mono truncate">
                      {d.deviceId}
                    </p>
                  </div>
                  {isSelected ? (
                    <Loader2 className="h-4 w-4 animate-spin text-[#8B1A1A] shrink-0" />
                  ) : (
                    <RssiBars bars={bars} />
                  )}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RssiBars({ bars }: { bars: 1 | 2 | 3 | 4 }) {
  return (
    <div className="flex items-end gap-0.5 shrink-0" aria-label={`Signal ${bars}/4`}>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`w-1 rounded-sm ${
            i <= bars ? "bg-[#8B1A1A]" : "bg-slate-200 dark:bg-[#454545]"
          }`}
          style={{ height: `${4 + i * 3}px` }}
        />
      ))}
    </div>
  );
}
